<template>
    <div class="wizard-step">
        <b-row>
            <b-col cols="12">
                <StepIndicator :current-step="currentStep" :config="config" class="mb-5" @show-terms="$emit('show-terms')" />
                <h3>Review & Consent</h3>
                <p class="mt-4">Please review your agreement details below and consent to electronic signature.</p>
            </b-col>
        </b-row>

        <!-- Agreement Summary in details table style -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card class="details-card">
                    <h5 class="mb-4 px-4 pt-3">Agreement Summary</h5>
                    <table class="details-table">
                        <tr v-for="(field, index) in (config.detailFields || [])" :key="'summary-' + index">
                            <th>{{field.label}}</th>
                            <td>
                                <template v-if="field.value">{{field.value}}</template>
                                <template v-else-if="field.format === 'currency'">{{plan[field.key] | currency}}</template>
                                <template v-else-if="field.format === 'date'">{{plan[field.key] | moment('MMMM Do, YYYY')}}</template>
                                <template v-else>{{plan[field.key]}}{{field.suffix || ''}}</template>
                            </td>
                        </tr>
                        <tr>
                            <th>Payment Method</th>
                            <td>
                                <span v-if="plan.ach_provided || plan.payment_method_id">
                                    <i class="fas fa-check-circle text-success mr-2"></i>
                                    <span v-if="plan.ach_nickname">{{plan.ach_nickname}}</span>
                                    <span v-else>ACH Bank Account</span>
                                    <span v-if="plan.ach_type"> ({{plan.ach_type === 'checking' ? 'Checking' : 'Savings'}}</span>
                                    <span v-if="plan.ach_account"> ****{{plan.ach_account.slice(-4)}})</span>
                                    <span v-else-if="plan.ach_last_four"> ****{{plan.ach_last_four}})</span>
                                    <span v-else-if="plan.ach_type">)</span>
                                </span>
                                <div v-else>
                                    <b-alert show variant="warning" class="mb-0">
                                        <i class="fas fa-exclamation-circle mr-2"></i>
                                        <strong>Important:</strong> You must add a payment method before your first payment is due. This can be done at any time once your account is established. It is your responsibility to keep your payment method up to date.
                                    </b-alert>
                                </div>
                            </td>
                        </tr>
                    </table>
                </b-card>
            </b-col>
        </b-row>

        <!-- Personal Information in details table style -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card class="details-card">
                    <h5 class="mb-4 px-4 pt-3">Personal Information</h5>
                    <table class="details-table">
                        <tr>
                            <th>Name</th>
                            <td>
                                <i class="fas fa-check-circle text-success mr-2"></i>
                                {{formData.first_name}} {{formData.last_name}}
                            </td>
                        </tr>
                        <tr>
                            <th>Primary Email</th>
                            <td>
                                <i class="fas fa-check-circle text-success mr-2"></i>
                                {{formData.email}}
                            </td>
                        </tr>
                        <tr v-if="formData.email_2">
                            <th>Secondary Email</th>
                            <td>
                                <i class="fas fa-check-circle text-success mr-2"></i>
                                {{formData.email_2}}
                            </td>
                        </tr>
                        <tr>
                            <th>Primary Phone</th>
                            <td>
                                <i class="fas fa-check-circle text-success mr-2"></i>
                                {{formData.phone | tel}}
                            </td>
                        </tr>
                        <tr v-if="formData.phone_2">
                            <th>Secondary Phone</th>
                            <td>
                                <i class="fas fa-check-circle text-success mr-2"></i>
                                {{formData.phone_2 | tel}}
                            </td>
                        </tr>
                        <tr>
                            <th>Address</th>
                            <td>
                                <i class="fas fa-check-circle text-success mr-2"></i>
                                {{formData.address}}<span v-if="formData.address_2">, {{formData.address_2}}</span><br>
                                {{formData.city}}, {{formData.state}} {{formData.zip}}
                            </td>
                        </tr>
                    </table>
                </b-card>
            </b-col>
        </b-row>

        <!-- Electronic Signature Disclosure -->
        <b-row class="mt-5 pt-3">
            <b-col cols="12">
                <b-card class="disclosure-card">
                    <h5><i class="fas fa-info-circle mr-2"></i>Electronic Signature Disclosure</h5>
                    <div class="disclosure-content mt-3">
                        <p>
                            Under the federal Electronic Signatures in Global and National Commerce Act (ESIGN Act)
                            and the Uniform Electronic Transactions Act (UETA), electronic signatures have the same
                            legal validity as handwritten signatures.
                        </p>
                        <p>
                            By proceeding to sign this agreement electronically:
                        </p>
                        <ul>
                            <li v-for="(item, index) in (config.esignDisclosureItems || [])" :key="index">{{item}}</li>
                        </ul>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- E-Sign Consent Section -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card class="consent-card">
                    <h5><i class="fas fa-file-signature mr-2"></i>Consent to Electronic Signature</h5>
                    <div class="consent-content mt-3">
                        <div class="checkbox-item">
                            <b-form-checkbox
                                v-model="esignConsent"
                                @change="onConsentChange"
                            >
                                <strong>I consent to sign this agreement electronically.</strong>
                                I understand that my electronic signature will be legally binding and enforceable,
                                with the same legal effect as a handwritten signature. I have reviewed the information
                                above and am ready to proceed with signing.
                            </b-form-checkbox>
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- What Happens Next -->
        <b-row class="mt-5 pt-2">
            <b-col cols="12">
                <b-card>
                    <h5>What Happens Next</h5>
                    <div class="next-steps mt-3">
                        <div class="step-item">
                            <i class="fas fa-file-alt text-primary fa-fw"></i>
                            <div>
                                <strong>Review Document</strong>
                                <p class="text-muted mb-0">On the next screen, you'll review your complete agreement document.</p>
                            </div>
                        </div>
                        <div class="step-item">
                            <i class="fas fa-signature text-primary fa-fw"></i>
                            <div>
                                <strong>Sign Electronically</strong>
                                <p class="text-muted mb-0">Draw your signature using your mouse, touchpad, or finger on mobile.</p>
                            </div>
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Validation info box -->
        <b-row class="mt-4" v-if="!isValid">
            <b-col cols="12">
                <b-alert show variant="warning">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>To continue:</strong> Please check the consent box above to proceed to signing.
                </b-alert>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import StepIndicator from '@/components/sign-agreement/StepIndicator.vue';

export default {
    name: 'ReviewAndConsent',
    components: {
        StepIndicator
    },
    props: {
        plan: {
            type: Object,
            required: true
        },
        formData: {
            type: Object,
            required: true
        },
        currentStep: {
            type: Number,
            required: true
        },
        config: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            esignConsent: false,
            consentTimestamp: null
        };
    },
    computed: {
        isValid() {
            return this.esignConsent;
        }
    },
    watch: {
        isValid(newVal) {
            this.$emit('validation-changed', newVal);
        }
    },
    mounted() {
        this.$emit('audit-event', {
            eventType: 'step_4_entered',
            data: { stepName: 'review_and_consent' }
        });
    },
    methods: {
        onConsentChange(checked) {
            if (checked) {
                this.consentTimestamp = new Date().toISOString();
                this.$emit('consent-given', {
                    timestamp: this.consentTimestamp
                });
                this.$emit('audit-event', {
                    eventType: 'consent_given',
                    data: { timestamp: this.consentTimestamp }
                });
            } else {
                this.consentTimestamp = null;
            }
        },

        getConsentData() {
            return {
                consentGiven: this.esignConsent,
                consentTimestamp: this.consentTimestamp
            };
        }
    }
};
</script>

<style lang="scss" scoped>
    @import '~@/assets/scss/_variables.scss';

    .details-card {
        padding: 0 !important;

        .card-body {
            padding: 0 !important;
        }

        h5 {
            color: #464577;
        }
    }

    .details-table {
        width: 100%;

        th {
            text-transform: uppercase;
            padding: 0.75rem 1.5rem;
            border-top: 1px solid $gray-300;
            font-size: 0.9rem;
            width: 30%;
            vertical-align: top;
            font-weight: 600;
            background-color: $gray-100;

            &.no-border {
                border-top: none;
            }
        }

        td {
            padding: 0.75rem 1.5rem;
            border-top: 1px solid $gray-300;
            vertical-align: top;
            width: 70%;
            font-size: 0.95rem;

            &.no-border {
                border-top: none;
            }

            .fa-check-circle {
                font-size: 0.9em;
            }
        }
    }

    .card {
        border: 1px solid #e9ecef;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);

        h5 {
            color: #464577;
            margin-bottom: 15px;
        }
    }

    .disclosure-card {
        border-left: 4px solid #17a2b8;

        .disclosure-content {
            p {
                color: #666;
                line-height: 1.6;
            }

            ul {
                color: #666;
                line-height: 1.8;
                padding-left: 20px;

                li {
                    margin-bottom: 5px;
                }
            }
        }
    }

    .consent-card {
        border-left: 4px solid #464577;

        .checkbox-item {
            padding: 15px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            transition: all 0.2s ease;

            &:hover {
                border-color: #46457776;
                box-shadow: 0 2px 4px rgba(70, 69, 119, 0.1);
            }

            &:has(input:checked),
            &:has(.custom-control-input:checked) {
                border-color: #464577;
                box-shadow: 0 2px 4px rgba(70, 69, 119, 0.1);
            }

            ::v-deep .custom-control-label {
                cursor: pointer;
                line-height: 1.5;

                strong {
                    display: block;
                    margin-bottom: 5px;
                    color: #333;
                }
            }
        }
    }

    .next-steps {
        .step-item {
            display: flex;
            align-items: flex-start;
            gap: 15px;
            padding: 15px 0;
            border-bottom: 1px solid #f0f0f0;

            &:last-child {
                border-bottom: none;
                padding-bottom: 0;
            }

            i {
                font-size: 1.2em;
                margin-top: 2px;
            }

            div {
                flex: 1;

                strong {
                    display: block;
                    color: #495057;
                    margin-bottom: 5px;
                }

                p {
                    line-height: 1.4;
                }
            }
        }
    }

    @media (max-width: 576px) {
        .details-table {
            th, td {
                display: block;
                width: 100%;
                padding: 0.5rem 1rem;
            }

            th {
                border-top: none;
                padding-bottom: 0;
                font-size: 0.8rem;
            }

            td {
                padding-top: 0.25rem;
                border-top: none;
                border-bottom: 1px solid $gray-200;
            }

            tr:last-child td {
                border-bottom: none;
            }
        }
    }
</style>
